import { inject, Injectable } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { ImageTransform } from 'ngx-image-cropper';

import { ArmyBuilderConfig } from '../../../global/config';
import { HttpClientWithInFlightCache } from '../../../global/httpClient';
import { ImageService } from '../../../global/images/image.service';
import { ImageCropperModal } from '../../../global/images/image-cropper.modal';

/**
 * Derived versions of an uploaded image. The API stores these in a subfolder of the
 * category folder under the original's filename, rather than beside the original under
 * a suffixed one. Must stay in step with `UPLOAD_VARIANTS` in the API's FileStorageService.
 */
export type ImageUploadVariant = 'cropped';

export interface CropConfig {
    width: number;
    height: number;
    minZoom?: number;
}

export interface UploadWithCropResult {
    imageId: string;
    imageSourceId: string;
    imageSourceFileName: string;
    previewUrl: string;
    zoom: number;
    transform: ImageTransform;
    cropWidth: number;
    cropHeight: number;
    cropX: number;
    cropY: number;
}

export interface ReCropResult {
    imageId: string;
    previewUrl: string;
    zoom: number;
    transform: ImageTransform;
    cropWidth: number;
    cropHeight: number;
    cropX: number;
    cropY: number;
}

@Injectable({ providedIn: 'root' })
export class ImageCropUploadService {
    private modalController = inject(ModalController);
    private http = inject(HttpClientWithInFlightCache);
    private config = inject(ArmyBuilderConfig);
    private imageService = inject(ImageService);

    /** Open the crop modal and return the cropped Blob with crop settings, or null if cancelled. */
    async openCropper(
        source: { imageBase64?: string; imageURL?: string | null },
        crop: CropConfig,
        initialZoom = 1,
        initialTransform?: ImageTransform,
        initialCropWidth?: number,
        initialCropHeight?: number
    ): Promise<{
        blob: Blob;
        zoom: number;
        transform: ImageTransform;
        cropWidth: number;
        cropHeight: number;
        cropX: number;
        cropY: number;
    } | null> {
        const modal = await this.modalController.create({
            component: ImageCropperModal,
            componentProps: {
                imageBase64: source.imageBase64,
                imageURL: source.imageURL,
                cropWidth: initialCropWidth ?? crop.width,
                cropHeight: initialCropHeight ?? crop.height,
                minZoom: crop.minZoom ?? 1,
                initialZoom,
                initialTransform: initialTransform ?? { scale: 1, translateH: 0, translateV: 0, translateUnit: 'px' },
                initialCropWidth,
                initialCropHeight
            },
            backdropDismiss: false
        });
        await modal.present();
        const { data } = await modal.onDidDismiss<{
            blob: Blob;
            zoom: number;
            transform: ImageTransform;
            cropWidth: number;
            cropHeight: number;
            cropX: number;
            cropY: number;
        } | null>();
        return data ?? null;
    }

    /** Upload a new image, open the cropper, and upload both original and cropped versions. */
    async uploadWithCrop(
        file: File,
        crop: CropConfig,
        gameId: string,
        category: string,
        initialZoom?: number,
        initialTransform?: ImageTransform,
        initialCropWidth?: number,
        initialCropHeight?: number
    ): Promise<UploadWithCropResult | null> {
        const imageBase64 = await this.fileToBase64(file);
        const result = await this.openCropper(
            { imageBase64 },
            crop,
            initialZoom ?? 1,
            initialTransform,
            initialCropWidth,
            initialCropHeight
        );
        if (!result) return null;

        const { blob: croppedBlob, zoom, transform, cropWidth, cropHeight, cropX, cropY } = result;
        // Same filename as the original -- the `cropped` variant folder is what separates them.
        const croppedFile = new File([croppedBlob], file.name, { type: croppedBlob.type });

        const [originalRes, croppedRes] = await Promise.all([
            this.uploadToApi(file, file.name, gameId, category),
            this.uploadToApi(croppedFile, croppedFile.name, gameId, category, 'cropped')
        ]);

        return {
            imageId: croppedRes.id,
            imageSourceId: originalRes.id,
            imageSourceFileName: file.name,
            previewUrl: URL.createObjectURL(croppedBlob),
            zoom,
            transform,
            cropWidth,
            cropHeight,
            cropX,
            cropY
        };
    }

    /** Open the cropper from the stored original image and upload the new cropped version. */
    async reCrop(
        sourceId: string,
        sourceFileName: string,
        crop: CropConfig,
        gameId: string,
        category: string,
        initialZoom?: number,
        initialTransform?: ImageTransform,
        initialCropWidth?: number,
        initialCropHeight?: number
    ): Promise<ReCropResult | null> {
        const imageURL = this.imageService.url(sourceId);
        const result = await this.openCropper({ imageURL }, crop, initialZoom ?? 1, initialTransform, initialCropWidth, initialCropHeight);
        if (!result) return null;

        const { blob: croppedBlob, zoom, transform, cropWidth, cropHeight, cropX, cropY } = result;
        const res = await this.uploadToApi(croppedBlob, sourceFileName, gameId, category, 'cropped');

        return {
            imageId: res.id,
            previewUrl: URL.createObjectURL(croppedBlob),
            zoom,
            transform,
            cropWidth,
            cropHeight,
            cropX,
            cropY
        };
    }

    uploadToApi(
        file: File | Blob,
        fileName: string,
        gameId: string,
        category: string,
        variant?: ImageUploadVariant
    ): Promise<{ id: string }> {
        const formData = new FormData();
        formData.append('file', file, fileName);
        formData.append('gameId', gameId);
        formData.append('category', category);
        if (variant) formData.append('variant', variant);
        const url = `${this.config.apiBaseUrl}/images/upload`;
        return this.http.post<{ id: string }>(url, formData).toPromise() as Promise<{ id: string }>;
    }

    private fileToBase64(file: File): Promise<string> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = () => reject(reader.error);
            reader.readAsDataURL(file);
        });
    }
}
